import { FUEL_PRICE_BASE_PER_GALLON } from '../simulation/economy';
import type { FuelFuture, FuelStorageFacility, GameState } from './types';

const clone = <T,>(value: T): T => structuredClone(value);

function touch(next: GameState): GameState {
  next.updatedAt = Date.now();
  next.ledger = next.ledger.slice(-1200);
  return next;
}

export const FUEL_STORAGE_CAPACITY_PER_FACILITY = 1_000_000;
export const FUEL_STORAGE_PRICE = 2_000_000;
export const FUEL_STORAGE_LIFE_WEEKS = 20 * 52; // 1040
export const FUEL_STORAGE_EXTENSION_WEEKS = 5 * 52; // 260
export const FUEL_STORAGE_EXTENSION_THRESHOLD = 10 * 52; // 520
export const FUEL_STORAGE_EXTENSION_PRICE = 200_000;
export const FUEL_SPOT_TRADE_MULTIPLIER = 0.97;

export function currentFuelPrice(state: GameState): number {
  return FUEL_PRICE_BASE_PER_GALLON * (state.lastReport?.fuelPriceIndex ?? 1);
}

export function currentFuelSpotTradePrice(state: GameState): number {
  return currentFuelPrice(state) * FUEL_SPOT_TRADE_MULTIPLIER;
}

function updateAverageCost(inventory: { gallons: number; averageCost: number }, addedGallons: number, addedCost: number): { gallons: number; averageCost: number } {
  if (inventory.gallons + addedGallons <= 0) return { gallons: 0, averageCost: 0 };
  const totalCost = inventory.gallons * inventory.averageCost + addedGallons * addedCost;
  return {
    gallons: inventory.gallons + addedGallons,
    averageCost: totalCost / (inventory.gallons + addedGallons),
  };
}

function totalPendingFutures(state: GameState): number {
  return state.fuelFutures.reduce((sum, future) => sum + (future.direction === 'short' ? 0 : future.quantity), 0);
}

export function totalFuelStorageCapacity(state: GameState): number {
  return state.fuelStorage
    .filter((facility) => facility.remainingLifeWeeks > 0)
    .reduce((sum, facility) => sum + facility.capacity, 0);
}

export const FUEL_STORAGE_PER_WEEK_LIMIT = 10;
export const FUEL_STORAGE_TOTAL_CAP = 300;

function activeFuelStorageCount(state: GameState): number {
  return state.fuelStorage.filter((facility) => facility.remainingLifeWeeks > 0).length;
}

export function nextFuelStoragePrice(state: GameState): number {
  const count = activeFuelStorageCount(state);
  if (count < 100) return FUEL_STORAGE_PRICE;
  const n = count + 1;
  return Math.round(FUEL_STORAGE_PRICE * (1 + (n - 1) * 0.1));
}

export function totalFuelStorageBookValue(state: GameState): number {
  return state.fuelStorage.reduce((sum, facility) => sum + facility.bookValue, 0);
}

function buildOneFuelStorage(state: GameState): GameState {
  const next = clone(state);
  const price = nextFuelStoragePrice(next);
  next.cash -= price;
  next.fuelStorage.push({
    id: `fuel-storage-${next.nextId++}`,
    capacity: FUEL_STORAGE_CAPACITY_PER_FACILITY,
    builtWeek: next.week,
    purchasePrice: price,
    bookValue: price,
    remainingLifeWeeks: FUEL_STORAGE_LIFE_WEEKS,
    totalLifeWeeks: FUEL_STORAGE_LIFE_WEEKS,
  });
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'capex',
    cashAmount: -price,
    profitAmount: 0,
    objectId: 'fuel-storage',
    label: `建造储油库 +${FUEL_STORAGE_CAPACITY_PER_FACILITY.toLocaleString('zh-CN')} 加仑`,
  });
  return next;
}

export function buildFuelStorage(state: GameState, count = 1): GameState {
  let next = clone(state);
  let remaining = Math.max(0, Math.floor(count));
  if (remaining === 0) return next;

  const currentTotal = activeFuelStorageCount(next) + next.fuelStorageQueue;
  const capRoom = Math.max(0, FUEL_STORAGE_TOTAL_CAP - currentTotal);
  remaining = Math.min(remaining, capRoom);
  if (remaining === 0) return next;

  const availableSlots = Math.max(0, FUEL_STORAGE_PER_WEEK_LIMIT - next.fuelStorageBuiltThisWeek);
  const immediateCount = Math.min(remaining, availableSlots);

  for (let i = 0; i < immediateCount; i += 1) {
    const price = nextFuelStoragePrice(next);
    if (next.cash < price) break;
    next = buildOneFuelStorage(next);
    next.fuelStorageBuiltThisWeek += 1;
    remaining -= 1;
  }

  if (remaining > 0) {
    next.fuelStorageQueue += remaining;
  }

  return touch(next);
}

export function cancelFuelStorageQueue(state: GameState): GameState {
  if (state.fuelStorageQueue <= 0) return state;
  const next = clone(state);
  next.fuelStorageQueue = 0;
  return touch(next);
}

export function processFuelStorageQueue(state: GameState): GameState {
  let next = clone(state);
  next.fuelStorageBuiltThisWeek = 0;

  const buildCount = Math.min(next.fuelStorageQueue, FUEL_STORAGE_PER_WEEK_LIMIT);
  let built = 0;
  for (let i = 0; i < buildCount; i += 1) {
    const price = nextFuelStoragePrice(next);
    if (next.cash < price) break;
    next = buildOneFuelStorage(next);
    built += 1;
  }

  next.fuelStorageQueue -= built;
  next.fuelStorageBuiltThisWeek = built;
  return touch(next);
}

export function sellOneFuelStorageFacility(state: GameState, facility: FuelStorageFacility): GameState {
  const next = clone(state);
  const salePrice = Math.round(facility.bookValue * 0.8);
  next.cash += salePrice;
  next.fuelStorage = next.fuelStorage.filter((f) => f.id !== facility.id);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'asset_sale',
    cashAmount: salePrice,
    profitAmount: salePrice - facility.bookValue,
    objectId: facility.id,
    label: `强制出售储油库（账面 ${Math.round(facility.bookValue).toLocaleString('zh-CN')}）`,
  });
  return next;
}

export function extendFuelStorageFacility(state: GameState, facilityId: string): GameState {
  const next = clone(state);
  const facility = next.fuelStorage.find((f) => f.id === facilityId);
  if (!facility) throw new Error('未找到该储油库');
  if (facility.remainingLifeWeeks >= FUEL_STORAGE_EXTENSION_THRESHOLD) {
    throw new Error(`该储油库剩余有效期 ${facility.remainingLifeWeeks} 周，无需延长`);
  }
  if (next.cash < FUEL_STORAGE_EXTENSION_PRICE) {
    throw new Error(`现金不足，延长储油库有效期需要 ${FUEL_STORAGE_EXTENSION_PRICE.toLocaleString('zh-CN')} 元`);
  }
  next.cash -= FUEL_STORAGE_EXTENSION_PRICE;
  facility.remainingLifeWeeks += FUEL_STORAGE_EXTENSION_WEEKS;
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'operating_expense',
    cashAmount: -FUEL_STORAGE_EXTENSION_PRICE,
    profitAmount: -FUEL_STORAGE_EXTENSION_PRICE,
    objectId: facility.id,
    label: `延长储油库有效期 ${FUEL_STORAGE_EXTENSION_WEEKS} 周`,
  });
  return next;
}

export function buyFuel(state: GameState, gallons: number): GameState {
  if (!Number.isFinite(gallons) || gallons <= 0 || Math.round(gallons) !== gallons) throw new Error('请输入有效的燃油数量');
  const next = clone(state);
  const capacity = totalFuelStorageCapacity(next);
  if (next.fuelInventory.gallons + gallons > capacity) {
    throw new Error(`库存容量不足，当前可用空间 ${(capacity - next.fuelInventory.gallons).toLocaleString('zh-CN')} 加仑`);
  }
  const price = currentFuelSpotTradePrice(next);
  const cost = price * gallons;
  if (next.cash < cost) throw new Error('公司现金不足以购买这些燃油');
  next.cash -= cost;
  next.fuelInventory = updateAverageCost(next.fuelInventory, gallons, price);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: -cost,
    profitAmount: 0,
    objectId: 'fuel',
    label: `购入燃油 ${gallons.toLocaleString('zh-CN')} 加仑`,
  });
  return next;
}

export function buyFuelFuture(state: GameState, gallons: number, deliveryWeeks: number): GameState {
  if (!Number.isFinite(gallons) || gallons <= 0 || Math.round(gallons) !== gallons) throw new Error('请输入有效的燃油数量');
  if (!Number.isFinite(deliveryWeeks) || deliveryWeeks < 1 || deliveryWeeks > 12 || Math.round(deliveryWeeks) !== deliveryWeeks) {
    throw new Error('交割周期需在 1-12 周之间');
  }
  const next = clone(state);
  const price = currentFuelPrice(next);
  const cost = price * gallons;
  if (next.cash < cost) throw new Error('公司现金不足以签订这份期货合约');
  next.cash -= cost;
  next.fuelFutures.push({
    id: `future-${next.nextId++}`,
    quantity: gallons,
    lockedPrice: price,
    deliveryWeek: next.week + deliveryWeeks,
    purchasedWeek: next.week,
    direction: 'long',
  });
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: -cost,
    profitAmount: 0,
    objectId: 'fuel-future',
    label: `签订燃油期货 ${gallons.toLocaleString('zh-CN')} 加仑，${deliveryWeeks} 周后交割`,
  });
  return next;
}

export function shortFuelFuture(state: GameState, gallons: number, deliveryWeeks: number): GameState {
  if (!Number.isFinite(gallons) || gallons <= 0 || Math.round(gallons) !== gallons) throw new Error('请输入有效的燃油数量');
  if (!Number.isFinite(deliveryWeeks) || deliveryWeeks < 1 || deliveryWeeks > 12 || Math.round(deliveryWeeks) !== deliveryWeeks) {
    throw new Error('交割周期需在 1-12 周之间');
  }
  const next = clone(state);
  const price = currentFuelPrice(next);
  const margin = price * gallons;
  if (next.cash < margin) throw new Error('公司现金不足以缴纳这份做空期货的保证金');
  next.cash -= margin;
  next.fuelFutures.push({
    id: `future-${next.nextId++}`,
    quantity: gallons,
    lockedPrice: price,
    deliveryWeek: next.week + deliveryWeeks,
    purchasedWeek: next.week,
    direction: 'short',
  });
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: -margin,
    profitAmount: 0,
    objectId: 'fuel-future',
    label: `做空燃油期货 ${gallons.toLocaleString('zh-CN')} 加仑，${deliveryWeeks} 周后交割`,
  });
  return next;
}

export function sellFuel(state: GameState, gallons: number): GameState {
  if (!Number.isFinite(gallons) || gallons <= 0 || Math.round(gallons) !== gallons) throw new Error('请输入有效的燃油数量');
  const next = clone(state);
  if (next.fuelInventory.gallons < gallons) throw new Error('燃油库存不足');
  const price = currentFuelSpotTradePrice(next);
  const proceeds = price * gallons;
  const costBasis = next.fuelInventory.averageCost * gallons;
  next.cash += proceeds;
  next.fuelInventory = updateAverageCost(next.fuelInventory, -gallons, next.fuelInventory.averageCost);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: proceeds,
    profitAmount: proceeds - costBasis,
    objectId: 'fuel',
    label: `卖出燃油 ${gallons.toLocaleString('zh-CN')} 加仑`,
  });
  return next;
}

export function sellFuelFuture(state: GameState, futureId: string): GameState {
  const next = clone(state);
  const index = next.fuelFutures.findIndex((item) => item.id === futureId);
  if (index < 0) throw new Error('未找到该期货合约');
  const future = next.fuelFutures[index];
  const price = currentFuelPrice(next);
  const isShort = future.direction === 'short';
  const notional = future.lockedPrice * future.quantity;
  const profit = (future.lockedPrice - price) * future.quantity;
  const settlement = isShort ? notional + profit : price * future.quantity;
  const costBasis = isShort ? notional : future.lockedPrice * future.quantity;
  next.cash += settlement;
  next.fuelFutures.splice(index, 1);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: settlement,
    profitAmount: isShort ? profit : settlement - costBasis,
    objectId: 'fuel-future',
    label: `${isShort ? '平仓做空' : '转让'}燃油期货 ${future.quantity.toLocaleString('zh-CN')} 加仑`,
  });
  return next;
}

function liquidateExcessFuel(state: GameState, excessGallons: number): { state: GameState; proceeds: number } {
  let next = clone(state);
  const price = currentFuelSpotTradePrice(next);
  const gallonsToSell = Math.min(excessGallons, next.fuelInventory.gallons);
  if (gallonsToSell <= 0) return { state: next, proceeds: 0 };
  const proceeds = price * gallonsToSell;
  const costBasis = next.fuelInventory.averageCost * gallonsToSell;
  next.cash += proceeds;
  next.fuelInventory = updateAverageCost(next.fuelInventory, -gallonsToSell, next.fuelInventory.averageCost);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'fuel',
    cashAmount: proceeds,
    profitAmount: proceeds - costBasis,
    objectId: 'fuel',
    label: `燃油库容不足，强制平仓卖出 ${Math.round(gallonsToSell).toLocaleString('zh-CN')} 加仑`,
  });
  return { state: next, proceeds };
}

export function settleFuelWeek(state: GameState, totalGallons: number, marketPriceMultiplier = 1): { state: GameState; cashCost: number } {
  let next = clone(state);
  let remainingGallons = totalGallons;
  let cashCost = 0;

  // 期货到期交割
  const dueFutures = next.fuelFutures.filter((f) => f.deliveryWeek <= next.week);
  next.fuelFutures = next.fuelFutures.filter((f) => f.deliveryWeek > next.week);
  for (const future of dueFutures) {
    if (future.direction === 'short') {
      const marketPrice = currentFuelPrice(next);
      const margin = future.lockedPrice * future.quantity;
      const profit = (future.lockedPrice - marketPrice) * future.quantity;
      next.cash += margin + profit;
      next.ledger.push({
        id: `ledger-${next.nextId++}`,
        week: next.week,
        category: 'fuel',
        cashAmount: margin + profit,
        profitAmount: profit,
        objectId: 'fuel-future',
        label: `做空燃油期货到期结算 ${future.quantity.toLocaleString('zh-CN')} 加仑`,
      });
    } else {
      next.fuelInventory = updateAverageCost(next.fuelInventory, future.quantity, future.lockedPrice);
    }
  }

  // 交割后若超出库容，强制平仓
  const capacity = totalFuelStorageCapacity(next);
  if (next.fuelInventory.gallons > capacity) {
    const excess = next.fuelInventory.gallons - capacity;
    const liquidation = liquidateExcessFuel(next, excess);
    next = liquidation.state;
  }

  // 消耗库存
  if (next.fuelInventory.gallons > 0) {
    const used = Math.min(remainingGallons, next.fuelInventory.gallons);
    cashCost += used * next.fuelInventory.averageCost;
    next.fuelInventory = updateAverageCost(next.fuelInventory, -used, next.fuelInventory.averageCost);
    remainingGallons -= used;
  }

  // 库存不足按市价购买；现金流出在 simulateWeek 的净现金流中统一扣减，避免重复记账。
  if (remainingGallons > 0) {
    const marketPrice = currentFuelPrice(next);
    cashCost += remainingGallons * marketPrice;
  }

  return { state: next, cashCost: cashCost * marketPriceMultiplier };
}
